import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TimeoutError, catchError, map, throwError, timeout } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../services/auth/auth';
import { environment } from '../../environments/environment';

const REQUEST_TIMEOUT_MS = 30000;
const CONNECTION_LOST_MESSAGE = 'Connection lost.';
const UNKNOWN_ERROR_MESSAGE = 'Unknown error occurred.';

function normalizeHttpError(error: unknown, requestUrl: string): HttpErrorResponse {
  if (error instanceof TimeoutError) {
    return new HttpErrorResponse({
      error: { message: CONNECTION_LOST_MESSAGE },
      status: 0,
      statusText: 'Request Timeout',
      url: requestUrl
    });
  }

  if (!(error instanceof HttpErrorResponse)) {
    return new HttpErrorResponse({
      error: { message: UNKNOWN_ERROR_MESSAGE },
      status: 0,
      statusText: 'Unknown Error',
      url: requestUrl
    });
  }

  const fallbackMessage = error.status === 0
    ? CONNECTION_LOST_MESSAGE
    : UNKNOWN_ERROR_MESSAGE;

  const serverMessage =
    error.error?.message ||
    error.error?.error?.details;

  return new HttpErrorResponse({
    error: {
      ...(typeof error.error === 'object' && error.error !== null ? error.error : {}),
      message: serverMessage || fallbackMessage
    },
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url || requestUrl
  });
}

function isExpectedApiResponse(body: unknown): boolean {
  return typeof body === 'object' &&
    body !== null &&
    typeof (body as { success?: unknown }).success === 'boolean';
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const toastr = inject(ToastrService);

  const token = authService.getToken();

  const authReq = token
    ? req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      })
    : req;

  return next(authReq).pipe(
    map(event => {
      if (
        authReq.url.startsWith(environment.apiBaseUrl) &&
        event instanceof HttpResponse &&
        !isExpectedApiResponse(event.body)
      ) {
        throw new HttpErrorResponse({
          error: { message: UNKNOWN_ERROR_MESSAGE },
          headers: event.headers,
          status: event.status,
          statusText: event.statusText,
          url: event.url || authReq.url
        });
      }

      return event;
    }),
    timeout(REQUEST_TIMEOUT_MS),
    catchError((error: unknown) => {
      const normalizedError = normalizeHttpError(error, authReq.url);

      if (normalizedError.status === 401) {
        authService.logout();

        toastr.error(
          'Your session has expired. Please login again.',
          'Session Expired'
        );

        router.navigate(['/auth/login']);
      }

      return throwError(() => normalizedError);
    })
  );
};
